import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { interval, startWith, switchMap, type Observable } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { setError, setLoaded, setLoading, withCallState } from '@ngrx-toolkit/core';
import { type IncidentsNotificationsInfo, type UnreadNotifications } from '../../../features/citation/shared/types';
import { NotificationsService } from '../../services/notifications';

type State = {
  notifications: Array<IncidentsNotificationsInfo>;
  total: number;
};

const initialState: State = {
  notifications: [],
  total: 0,
};
const POLLING_INTERVAL = 60000;

export const UnreadNotificationsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCallState(),
  withMethods((store, api = inject(NotificationsService)) => {
    const request = (ids: Array<string>): Observable<UnreadNotifications> =>
      api.getUnreadNotifications(ids).pipe(
        tapResponse({
          next: (res) =>
            patchState(
              store,
              {
                total: res.totalUnreadNotifications,
                notifications: res.incidentsUnreadNotifications,
              },
              setLoaded(),
            ),
          error: (error) => {
            if (!store.loaded()) patchState(store, setError(error));
          },
        }),
      );

    return {
      load: rxMethod<Array<string>>(
        switchMap((ids) => {
          patchState(store, setLoading());
          return request(ids);
        }),
      ),
      poll: rxMethod<Array<string>>(
        switchMap((ids) => {
          patchState(store, setLoading());
          return interval(POLLING_INTERVAL).pipe(
            startWith(0),
            switchMap(() => request(ids)),
          );
        }),
      ),
    };
  }),
);
